import buildQuery from "odata-query";
import { IApiRequest } from "@miplatform/types";
import { buildQueryString, getRootUrl } from "@miplatform/utils";
import { HTTP_METHODS } from "../../constants";

interface IApiOptionsProps<T> extends IApiRequest<T> {
  url: string;
  method?: string;
  modifiedOn?: string;
  etag?: string;
  headers?: {
    "if-none-match"?: string;
    "if-match"?: string;
    [key: string]: string | undefined;
  };
  customFilterString?: string;
  responseType?: string;
  query?: { [key: string]: unknown };
}

export async function apiOptions<T>(props: IApiOptionsProps<T>) {
  const {
    url,
    method = "get",
    modifiedOn,
    data,
    etag,
    headers,
    filters,
    customFilterString,
    sort,
    params,
    responseType,
    signal,
    top,
    skip,
    query,
  } = props;
  let rootUrl = "";
  if (!url.includes("http")) {
    rootUrl = await getRootUrl();
  }

  const queryParams = buildQuery({
    filter: filters,
    orderBy: sort,
    top,
    skip,
  });

  let queryString = "";
  if (query && queryParams) {
    queryString = `${buildQueryString(query)}&${queryParams.slice(1)}`; // remove the "?" at the beginning of queryParams
  } else if (query && queryParams.length === 0) {
    queryString = buildQueryString(query);
  } else {
    queryString = queryParams;
  }

  const options: {
    method: string;
    url: string;
    params?: { [key: string]: string | number | boolean };
    headers: { "Content-Type": string } & { [key: string]: string | undefined };
    responseType?: string;
    signal?: AbortSignal;
    [key: string]: unknown;
  } = {
    method: method.toLowerCase(),
    url: `${rootUrl}${url}${queryString}`,
    params,
    headers: {
      "Content-Type": "application/json",
    },
    responseType,
    signal,
  };

  if (customFilterString) {
    options.url = options.url.concat(`&${customFilterString}`);
  }

  if (etag) {
    options.headers["if-none-match"] = etag;
  }

  if (
    [HTTP_METHODS.PUT, HTTP_METHODS.PATCH, HTTP_METHODS.DELETE].includes(
      options.method
    )
  ) {
    options.headers["if-match"] = data?.modifiedOn || modifiedOn;
  }

  if (headers) {
    options.headers = { ...options.headers, ...headers };
  }

  switch (options.method) {
    case HTTP_METHODS.POST:
    case HTTP_METHODS.PATCH:
    case HTTP_METHODS.PUT:
    case HTTP_METHODS.DELETE:
      return {
        ...options,
        data,
      };
    case "get":
    default:
      return options;
  }
}
